import React, {createContext, FC, useCallback, useContext, useState} from 'react';
import {tryJSONparse, tryJSONStringify, isBrowser} from './functions';

interface LocalStorageContextType {
    localStorage: Record<string, any>;
    getItem: (key: string) => any;
    removeItem: (key: string) => void;
    setItem: (params: {key: string; value: any}) => void;
}
const LocalStorageContext = createContext({} as LocalStorageContextType);

interface LocalStorageProviderProps {
    withState?: boolean;
    onChange?: (localStorage: Record<string, any>) => void;
    children?: React.ReactNode;
}

const LocalStorageProvider: FC<LocalStorageProviderProps> = ({withState = true, onChange, children}) => {
    const getLocalStorage = useCallback((): Record<string, any> => {
        if (!isBrowser()) return {};
        const localStorageKeys = Object.keys(window.localStorage);
        const localStorage: Record<string, any> = {};
        localStorageKeys.forEach((key) => {
            const value = window.localStorage[key];
            localStorage[key] = tryJSONparse(value);
        });
        return localStorage;
    }, []);

    const [localStorage, setLocalStorage] = useState<Record<string, any>>(getLocalStorage());

    const setItem: (params: {key: string; value: any}) => void = useCallback(
        ({key, value: _value}) => {
            if (!key) throw new Error('No key passed');

            const value = tryJSONparse(_value);
            window.localStorage.setItem(key, tryJSONStringify(_value));
            if (withState) {
                setLocalStorage((old) => {
                    const newLocalStorage = {...old, [key]: value};
                    if (onChange) onChange(newLocalStorage);
                    return newLocalStorage;
                });
            } else {
                if (onChange) onChange(getLocalStorage());
            }
        },
        [onChange, withState, getLocalStorage]
    );

    const getItem: (key: string) => any = useCallback(
        (key) => {
            if (!key) throw new Error('No key passed');
            if (withState) return localStorage[key];
            else return getLocalStorage()[key];
        },
        [localStorage, withState, getLocalStorage]
    );

    const removeItem: (key: string) => void = useCallback(
        (key) => {
            if (!key) throw new Error('No key passed');
            window.localStorage.removeItem(key);
            if (withState)
                setLocalStorage((old) => {
                    const newLocalStorage = {...old};
                    delete (newLocalStorage as Record<string, any>)[key];
                    if (onChange) onChange(newLocalStorage);
                    return newLocalStorage;
                });
            else if (onChange) onChange(getLocalStorage());
        },
        [onChange, withState, getLocalStorage]
    );

    return (
        <LocalStorageContext.Provider
            value={{
                localStorage,
                getItem,
                setItem,
                removeItem,
            }}>
            {children}
        </LocalStorageContext.Provider>
    );
};

export const useLocalStorageContext = () => {
    const context = useContext(LocalStorageContext);
    if (context === undefined) {
        throw new Error('useLocalStorageContext must be used within an LocalStorageContext.Provider');
    }
    return context;
};

export default LocalStorageProvider;
